import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import {
  type ButtonHTMLAttributes,
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import { Icon } from '../display/Icon.tsx';
import styles from './Button.module.css';
import { Spinner } from './Spinner.tsx';

export type ButtonVariant = 'primary' | 'tonal' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant | undefined;
  readonly size?: ButtonSize | undefined;
  readonly icon?: LucideIcon | undefined;
  /** Shows progress and blocks repeated presses while an action runs. */
  readonly loading?: boolean | undefined;
  readonly fullWidth?: boolean | undefined;
  /** Styles the single child element (e.g. a router link) as a button instead of rendering one. */
  readonly asChild?: boolean | undefined;
  readonly ref?: Ref<HTMLButtonElement> | undefined;
}

export const buttonClassName = ({
  variant = 'secondary',
  size = 'md',
  fullWidth = false,
}: Pick<ButtonProps, 'variant' | 'size' | 'fullWidth'>): string =>
  clsx(styles.button, styles[variant], styles[size], fullWidth && styles.fullWidth);

const Content = ({
  icon,
  loading,
  children,
}: {
  icon?: LucideIcon | undefined;
  loading: boolean;
  children: ReactNode;
}) => (
  <>
    {loading ? <Spinner className={styles.spinner} /> : icon ? <Icon icon={icon} size="sm" /> : null}
    {children !== undefined && children !== null && <span className={styles.label}>{children}</span>}
  </>
);

export const Button = ({
  variant,
  size,
  icon,
  loading = false,
  fullWidth,
  asChild = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) => {
  const classes = clsx(buttonClassName({ variant, size, fullWidth }), className);
  if (asChild && isValidElement<{ className?: string; children?: ReactNode }>(children)) {
    const child: ReactElement<{ className?: string; children?: ReactNode }> = children;
    return cloneElement(child, {
      className: clsx(classes, child.props.className),
      children: (
        <Content icon={icon} loading={loading}>
          {child.props.children}
        </Content>
      ),
    });
  }
  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      <Content icon={icon} loading={loading}>
        {children}
      </Content>
    </button>
  );
};
